import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Terms of Service | Healing Lotus Skin Spa"
        description="Terms of service for Healing Lotus Skin Spa's website, bookings, and membership."
        path="/terms-of-service"
        noindex
      />
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8 md:p-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#3A5E63] font-serif mb-2">Terms of Service</h1>
          <p className="text-sm text-[#344854]/70">Last updated: January 4, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Agreement to Terms</h2>
            <p className="text-[#344854] leading-relaxed">
              By accessing and using the Healing Lotus Spa website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Services Provided</h2>
            <p className="text-[#344854] leading-relaxed mb-3">Healing Lotus Spa provides:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2">
              <li>Online appointment booking and management</li>
              <li>Spa and wellness services at our facility</li>
              <li>Digital wallet and payment services</li>
              <li>Membership programs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Account Registration</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              To use certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-[#344854] space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Booking and Cancellation Policy</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-[#344854] mb-2">Appointments</h3>
                <ul className="list-disc list-inside text-[#344854] space-y-2">
                  <li>Appointments must be booked in advance through our website or by phone</li>
                  <li>We recommend arriving 10-15 minutes early for your appointment</li>
                  <li>Late arrivals may result in shortened service time</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#344854] mb-2">Cancellations</h3>
                <ul className="list-disc list-inside text-[#344854] space-y-2">
                  <li>Cancellations must be made at least 24 hours in advance</li>
                  <li>Late cancellations or no-shows may incur a fee</li>
                  <li>Repeated no-shows may result in account suspension</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Payment Terms</h2>
            <div className="space-y-3">
              <p className="text-[#344854] leading-relaxed">
                <strong>Payment Methods:</strong> We accept major credit cards, digital wallets, and prepaid wallet balance.
              </p>
              <p className="text-[#344854] leading-relaxed">
                <strong>Pricing:</strong> All prices are in USD and subject to change. Current pricing is displayed at time of booking.
              </p>
              <p className="text-[#344854] leading-relaxed">
                <strong>Wallet Credits:</strong> Prepaid wallet funds are non-refundable but can be used for any services. Bonus credits may have expiration dates.
              </p>
              <p className="text-[#344854] leading-relaxed">
                <strong>Refunds:</strong> Refunds are processed at our discretion. Approved refunds are returned to the original payment method within 5-10 business days.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Membership Programs</h2>
            <p className="text-[#344854] leading-relaxed mb-3">If you purchase a membership:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2">
              <li>Membership benefits are as described at time of purchase</li>
              <li>Memberships are non-transferable</li>
              <li>We reserve the right to modify membership terms with notice</li>
              <li>Cancellation policies vary by membership type</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Health and Safety</h2>
            <p className="text-[#344854] leading-relaxed mb-3">For your safety and the safety of others:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2">
              <li>Inform us of any health conditions, allergies, or injuries before treatment</li>
              <li>Follow all staff instructions and safety guidelines</li>
              <li>We reserve the right to refuse service if we believe it poses a health risk</li>
              <li>Consult your physician before spa treatments if you have medical concerns</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Conduct and Behavior</h2>
            <p className="text-[#344854] leading-relaxed mb-3">We maintain a respectful, peaceful environment. You agree to:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2">
              <li>Treat staff and other guests with respect and courtesy</li>
              <li>Maintain appropriate conduct at all times</li>
              <li>Silence mobile devices during treatments</li>
              <li>Not engage in disruptive or inappropriate behavior</li>
            </ul>
            <p className="text-[#344854] leading-relaxed mt-3">
              Violation of conduct policies may result in immediate termination of services and account suspension.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Intellectual Property</h2>
            <p className="text-[#344854] leading-relaxed">
              All content on our website, including text, graphics, logos, images, and software, is the property of Healing Lotus Spa and protected by copyright and trademark laws. You may not reproduce, distribute, or use any content without our written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Limitation of Liability</h2>
            <p className="text-[#344854] leading-relaxed">
              Healing Lotus Spa provides services "as is" and makes no warranties, express or implied. We are not liable for any indirect, incidental, or consequential damages arising from use of our services. Our total liability is limited to the amount you paid for the specific service in question.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Account Termination</h2>
            <p className="text-[#344854] leading-relaxed">
              We reserve the right to suspend or terminate your account at any time for violations of these Terms of Service, fraudulent activity, or other conduct we deem harmful to our business or other users. You may close your account at any time through your profile settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Changes to Terms</h2>
            <p className="text-[#344854] leading-relaxed">
              We may update these Terms of Service from time to time. Significant changes will be communicated via email or website notice. Continued use of our services after changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Governing Law</h2>
            <p className="text-[#344854] leading-relaxed">
              These Terms of Service are governed by the laws of the State of Texas, United States. Any disputes shall be resolved in the courts of Texas.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Contact Information</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              Questions about these Terms of Service? Contact us:
            </p>
            <div className="bg-[#F8F6F2] p-4 rounded-md">
              <p className="text-[#344854]"><strong>Healing Lotus Spa</strong></p>
              <p className="text-[#344854]">Email: support@lotushealingspa.com</p>
              <p className="text-[#344854]">Website: https://lotushealingspa.com</p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-[#B2D8C5]">
          <Link
            to="/"
            className="inline-flex items-center text-[#3A5E63] hover:text-[#2d4a4d] transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
